import { tenant } from '@tenant';
import Select from 'antd/es/select';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { fieldToDisplayableName } from 'utils/formatters/fieldToDisplayableName';

import type { FieldObject } from 'api';

import { InputError } from '@atoms/InputError/InputError';
import { Label } from '@atoms/Label';
import { TooltipIconWrapper } from '@atoms/TooltipIconWrapper';
import { Wrap } from '@atoms/Wrap';

import { BusinessUnitSelector } from '@molecules/BusinessUnitSelector';
import { IBANInput } from '@molecules/IBANInput';
import { InputField } from '@molecules/InputField';
import { PurposeCodeSelector } from '@molecules/PurposeCodeSelector';
import { SelectCountry } from '@molecules/SelectCountry';
import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import { SeparatedDateField } from './SeparatedDateField';

type Props = {
  fieldName: string;
  fieldDefinition: FieldObject;
  form: UseFormReturn<any, any>;
  fieldToDisplayableNameSchema?: string;
  ['data-testid']?: string;
  responseField?: string;
};

const e2eTestCaseChoices = [
  { value: 'HOLD', label: 'Hold' },
  { value: 'RETURNED', label: 'Returned' }
];

export const SpecificField = ({
  fieldName,
  fieldDefinition,
  form,
  ['data-testid']: dataTestId,
  fieldToDisplayableNameSchema = 'FIELD_NAMES',
  responseField
}: Props) => {
  const fieldDisplayableName = fieldToDisplayableName(
    fieldName,
    fieldToDisplayableNameSchema
  );
  const { errors, value, ui_field: uiField } = fieldDefinition;

  const { control, formState } = form;
  const formError = formState.errors[fieldName]?.message as string;

  const errorMessage =
    formError ||
    errors?.map((error) => `${fieldDisplayableName} ${error}`).join('. ');

  const { onChange, ...inputProps } = form.register(fieldName, { value });

  const textField = (fieldName: string) => {
    switch (fieldName) {
      case 'virtual_account_number':
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange } }) => (
              <>
                <SelectVirtualAccount
                  onChangeCallback={(accountNumber: string) =>
                    onChange(accountNumber)
                  }
                  currentAccountNumber={
                    form.getValues(fieldName) ?? value?.toString()
                  }
                  className="!h-12 !w-full"
                  allowAll={false}
                  e2eId={dataTestId}
                  filterTerminatedAccounts={true}
                  filterSuspendedAccounts={true}
                  filterCryptoAccounts={true}
                />
                <InputError errorMessage={errorMessage} />
              </>
            )}
          />
        );
      case 'country_code':
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <SelectCountry
                  onChange={onChange}
                  value={value}
                  data-testid={dataTestId}
                />
                <InputError errorMessage={errorMessage} />
              </>
            )}
          />
        );
      case 'type':
        // Show dropdown if this is Receiver.type
        return responseField === 'creditor' ? (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <Select
                  value={value}
                  options={[
                    { value: 'private', label: 'Private' },
                    { value: 'organization', label: 'Organization' }
                  ]}
                  onChange={onChange}
                  data-testid={dataTestId}
                />
                <InputError errorMessage={errorMessage} />
              </>
            )}
          />
        ) : (
          <InputField
            errorMessage={errorMessage}
            onChange={onChange}
            {...inputProps}
            data-testid={dataTestId}
          />
        );
      case 'e2e_test_case':
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <Select
                  value={value}
                  options={e2eTestCaseChoices}
                  onChange={onChange}
                  data-testid={dataTestId}
                />
                <InputError errorMessage={errorMessage} />
              </>
            )}
          />
        );
      case 'website':
        return (
          <>
            <InputField
              errorMessage={errorMessage}
              onChange={onChange}
              {...inputProps}
              data-testid={dataTestId}
            />
            <span className="pl-2 text-xs text-content-subtle">
              E.g.: {tenant.domains.root}
            </span>
          </>
        );
      case 'iban':
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange } }) => (
              <>
                <IBANInput
                  onChange={onChange}
                  data-testid={dataTestId}
                  value={form.getValues(fieldName)}
                />
                <span className="pl-2 text-xs text-content-subtle">
                  E.g.: GB29 NWBK 6016 1331 9268 19
                </span>
                <InputError errorMessage={errorMessage} />
              </>
            )}
          />
        );
      case 'business_unit':
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange } }) => (
              <BusinessUnitSelector
                onChange={(businessUnitId) => onChange(businessUnitId)}
                errorMessage={errorMessage}
                value={form.getValues(fieldName)}
                data-testid={dataTestId}
              />
            )}
          />
        );

      case 'purpose_code':
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { onChange } }) =>
              fieldDefinition.rules?.external_code == 'B2B Purpose Codes' ? (
                <PurposeCodeSelector
                  onChange={(purposeCode) => onChange(purposeCode)}
                  value={form.getValues(fieldName) ?? value?.toString()}
                  data-testid={dataTestId}
                  className="!h-12 !w-full"
                />
              ) : (
                <InputField
                  errorMessage={errorMessage}
                  onChange={onChange}
                  {...inputProps}
                  data-testid={dataTestId}
                />
              )
            }
          />
        );

      default:
        return (
          <InputField
            errorMessage={errorMessage}
            onChange={onChange}
            {...inputProps}
            data-testid={dataTestId}
          />
        );
    }
  };

  const getFieldExplanation = (fieldName: string) => {
    switch (fieldName) {
      case 'end_to_end_id':
        return 'Enter a unique ID (letters, numbers, - or _). It will follow the payment to the recipient and help you with reconciliation or tracking.';
      case 'purpose_code':
        // The "purpose_code" we want to show is only related to the non-B2B purpose code
        return fieldDefinition.rules?.external_code == 'B2B Purpose Codes'
          ? undefined
          : "Enter the required code for this payment's purpose. If the value is invalid, the correct code will be shown in the error message.";
      case 'bank_clearing_system_id':
        return 'The unique identifier for the local clearing system that processes this payment. Use the correct ID for the destination country.';
      case 'bank_clearing_system_code':
        return 'The standard code for the clearing system through which the payment will be routed. If the value is invalid, the correct code will be shown in the error message.';
      default:
        return;
    }
  };

  const fieldExplanation = getFieldExplanation(fieldName);

  return (
    <div data-testid={dataTestId}>
      <Wrap
        if={!!fieldDisplayableName}
        with={Label}
        wrapperProps={{
          key: `pr-field-${fieldName}`,
          text: (
            <Wrap
              if={!!fieldExplanation}
              with={TooltipIconWrapper}
              wrapperProps={{
                tooltipTitle: fieldExplanation,
                placement: 'topLeft'
              }}
            >
              {fieldDisplayableName}
            </Wrap>
          ),
          className: 'mb-4'
        }}
      >
        {uiField == 'datepicker' ? (
          <Controller
            name={fieldName}
            control={form.control}
            render={({ field: { onChange, value } }) => (
              <SeparatedDateField
                errorMessage={errorMessage}
                onChange={onChange}
                defaultValue={value}
                data-testid={dataTestId}
              />
            )}
          />
        ) : (
          textField(fieldName)
        )}
      </Wrap>
    </div>
  );
};
